import React, { useRef, useCallback, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { Grip } from 'lucide-react';
import { Typography } from '@growthaxl/web-foundation';
import { getActiveProductLine } from '../../../adapters/AdapterRegistry';
import { useAuth } from '../../Auth';
import { useShellStore, resolveEsaPanelRatio } from '../../../shell/store/shellStore';
import { useShellBootstrap } from '../../../common/components/ShellBootstrapContext';
import { resolveCatalogApplicationUkeyFromWaffleItem } from '../../Esa/utils/moduleApplicationMap';
import { fetchAndPublishScopedCatalog } from '../../Esa/utils/pksSolutionsCatalog';
import { useGlobalHeaderState } from '../hooks/useGlobalHeaderState';
import { useActiveModuleLabel } from '../hooks/useActiveModuleLabel';
import { deriveInitials } from '../utils/deriveInitials';
import { resolveLucideIcon } from '../utils/resolveLucideIcon';
import { EdbBrandMark } from './EdbBrandMark';
import { WaffleMenu } from './WaffleMenu';
import { UserMenu } from './UserMenu';
import type { WaffleItem } from '../data/types';

export interface GlobalHeaderProps {
  className?: string;
}

export function GlobalHeader({ className }: GlobalHeaderProps): React.ReactElement {
  const { user } = useAuth();
  const { catalogReady } = useShellBootstrap();
  const { waffleOpen, userMenuOpen, toggleWaffle, toggleUserMenu, closeAll } =
    useGlobalHeaderState();
  const navigate = useNavigate();
  const avatarRef = useRef<HTMLButtonElement>(null);
  const initials = deriveInitials(user?.name);
  const userName = user?.name ?? 'Guest';
  const productLine = getActiveProductLine();
  const headerTitle = productLine?.label ?? 'GrowthAXL';
  const waffleEnabled = productLine?.features?.waffleMenu !== false;
  const brandedHeader = productLine?.features?.brandedModuleHeader !== false;
  const activeModule = useActiveModuleLabel();

  useEffect(() => {
    document.title = headerTitle;
    if ((!catalogReady || !waffleEnabled) && waffleOpen) {
      closeAll();
    }
  }, [headerTitle, catalogReady, waffleEnabled, waffleOpen, closeAll]);

  const handleSelectItem = useCallback(
    (item: WaffleItem, applicationId: string) => {
      if (!waffleEnabled || !catalogReady) return;
      closeAll();
      const store = useShellStore.getState();
      const setModule = store.setModuleContext;
      const setSolution = store.setSolutionContext;
      const setEsaPanelRatio = store.setEsaPanelRatio;

      if (item.type === 'module') {
        setModule(item.id, applicationId);
      } else {
        setSolution(item.id, applicationId);
      }

      setEsaPanelRatio(resolveEsaPanelRatio(
        item.type === 'module' ? item.id : null,
        item.type === 'solution' ? item.id : null,
      ));

      // Always refetch catalog WITH application_ukey on waffle click so the
      // Runs dropdown gets app-scoped solutions (independent of React deps).
      const tenantId = store.tenant?.id ?? '';
      const applicationUkey = resolveCatalogApplicationUkeyFromWaffleItem(item);
      if (tenantId && applicationUkey) {
        void fetchAndPublishScopedCatalog(tenantId, applicationUkey);
      }

      if (item.route) {
        navigate(item.route);
      }
    },
    [waffleEnabled, catalogReady, closeAll, navigate],
  );

  return (
    <>
      <header
        data-testid="global-header"
        style={{ height: 48, minHeight: 48, maxHeight: 48 }}
        className={`flex shrink-0 items-center justify-between border-b border-border-default bg-esa-panel px-4 ${className ?? ''}`}
      >
        {brandedHeader ? (
          <div className="flex min-w-0 flex-1 items-center gap-[21px] overflow-hidden">
            <div data-testid="global-header-logo" className="flex flex-shrink-0 items-center gap-2">
              <EdbBrandMark />
              <Typography variant="subtitle1" component="span" className="font-bold tracking-tight">
                EDB
              </Typography>
            </div>
            <span className="h-4 w-px flex-shrink-0 bg-border-default" aria-hidden="true" />
            <div className="flex min-w-0 items-center gap-[18px] overflow-hidden" data-testid="global-header-module">
              {activeModule.icon && (
                <span className="flex flex-shrink-0 items-center justify-center leading-none" aria-hidden>
                  {resolveLucideIcon(activeModule.icon)}
                </span>
              )}
              <Typography
                variant="subtitle1"
                component="span"
                data-testid="global-header-module-name"
                className="truncate font-semibold tracking-tight"
              >
                {activeModule.name}
              </Typography>
            </div>
          </div>
        ) : (
          <Typography
            variant="subtitle1"
            component="span"
            data-testid="global-header-title"
            className="min-w-0 truncate font-semibold tracking-tight"
          >
            {headerTitle}
          </Typography>
        )}

        <div className="flex flex-shrink-0 items-center gap-2">
          {waffleEnabled && (
            <button
              data-testid="global-header-waffle-btn"
              aria-expanded={waffleOpen}
              aria-haspopup="dialog"
              aria-label="Module selector"
              aria-disabled={!catalogReady}
              disabled={!catalogReady}
              onClick={catalogReady ? toggleWaffle : undefined}
              className={`flex h-8 w-8 items-center justify-center rounded-6 text-text-secondary transition-colors duration-fast ${
                catalogReady
                  ? 'cursor-pointer hover:bg-surface-hover'
                  : 'cursor-not-allowed opacity-40'
              }`}
            >
              <Grip size={20} strokeWidth={1.5} />
            </button>
          )}

          <div className="relative">
            <button
              ref={avatarRef}
              data-testid="global-header-avatar-btn"
              aria-expanded={userMenuOpen}
              aria-haspopup="menu"
              aria-label={`${userName} menu`}
              onClick={toggleUserMenu}
              className="flex h-8 w-8 cursor-pointer items-center justify-center rounded-full bg-brand-primary text-caption font-semibold text-brand-on-primary transition-colors duration-fast hover:bg-brand-primary-hover"
            >
              {initials}
            </button>

            <UserMenu open={userMenuOpen} onClose={closeAll} anchorRef={avatarRef} />
          </div>
        </div>
      </header>

      {waffleEnabled && (
        <WaffleMenu
          open={catalogReady && waffleOpen}
          onClose={closeAll}
          onSelectItem={handleSelectItem}
        />
      )}
    </>
  );
}
